@file:UseSerializers(InstantAsEpochMillis::class)
package net.frozenbit.games.soundgame.dtos

import kotlinx.serialization.Serializable
import kotlinx.serialization.UseSerializers
import net.frozenbit.games.soundgame.dtos.serializers.InstantAsEpochMillis
import kotlin.time.Instant

/**
 * Server→Host DTOs
 */
@Serializable
sealed interface HostServerMessage

@Serializable
data class GameCreationFailed(
    val reason: String,
) : HostServerMessage

@Serializable
data object HostIdleUpdate : HostServerMessage

@Serializable
data class HostCommonGameData(
    val sessionId: String,
    val joinCode: String,
    val serverTime: Instant,
)

@Serializable
data class HostLobbyUpdate(
    val common: HostCommonGameData,
    val players: List<WsPlayer>,
) : HostServerMessage

@Serializable
data class HostPromptSelectionUpdate(
    val common: HostCommonGameData,
    val startedAt: Instant,
    val expiresAt: Instant,
    val selectingPlayer: WsPlayer,
) : HostServerMessage

@Serializable
data class HostSoundSelectionUpdate(
    val common: HostCommonGameData,
    val startedAt: Instant,
    val expiresAt: Instant,
    val jury: WsPlayer,
    val participants: List<Participant>,
    val prompt: WsPrompt,
) : HostServerMessage {
    @Serializable
    data class Participant(
        val player: WsPlayer,
        val picked: Boolean,
    )
}

@Serializable
data class HostVotingUpdate(
    val common: HostCommonGameData,
    val startedAt: Instant?,
    val expiresAt: Instant?,
    val jury: WsPlayer,
    val prompt: WsPrompt,
    val votingOpen: Boolean,
    val options: List<Creation>,
) : HostServerMessage {
    @Serializable
    data class Creation(
        val sound1Id: String,
        val sound1Title: String,
        val sound2Id: String,
        val sound2Title: String,
    )
}

@Serializable
data class HostReplayVoteOption(
    /**
     * Referring to index in [HostVotingUpdate.options]
     */
    val optionIndex: Int,
): HostServerMessage

@Serializable
data class HostWinnerUpdate(
    val common: HostCommonGameData,
    val prompt: WsPrompt,
    val jury: WsPlayer,
    /**
     * Same order and size as [HostVotingUpdate.options]
     */
    val entries: List<Entry>,
) : HostServerMessage {
    @Serializable
    data class Entry(
        val author: WsPlayer,
        val points: Int,
        val winner: Boolean,
        val sound1Id: String,
        val sound1Title: String,
        val sound2Id: String,
        val sound2Title: String,
    )
}

@Serializable
data class HostGameOverUpdate(
    val common: HostCommonGameData,
    val scores: List<PlayerScore>,
    val winnerIndex: Int,
    val creditText: String,
) : HostServerMessage {
    @Serializable
    data class PlayerScore(
        val player: WsPlayer,
        val score: Int,
    )
}

@Serializable
data class WsPlayer(
    val name: String,
    val avatarIndex: Int,
    val offline: Boolean,
)

@Serializable
data class WsPrompt(
    val id: String,
    val text: String,
)